import { DomainError } from './domain.error';

/**
 * Thrown when an on-demand crop names a region that is not a valid normalized
 * box: a coordinate outside [0, 1], a minimum above its maximum, or something
 * that is not a number at all.
 *
 * Never retriable - the request itself is wrong, and the same request will be
 * wrong the same way every time.
 */
export class InvalidCropRegionError extends DomainError {
  readonly httpStatus = 400;
  readonly code = 'INVALID_CROP_REGION';

  constructor(message: string) {
    super(message, false);
  }
}

/** Thrown when the asset behind an on-demand crop request does not exist. */
export class CropAssetNotFoundError extends DomainError {
  readonly httpStatus = 404;
  readonly code = 'CROP_ASSET_NOT_FOUND';

  constructor(assetId: string) {
    super(`Asset not found: ${assetId}`, false);
  }
}

/**
 * Thrown when the asset exists but has no tile pyramid to crop from - the DZI
 * generation has not run, failed, or the asset is not a tiled image at all.
 *
 * A 404 rather than a 409 on purpose: clients retry 409s, and retrying does not
 * make a pyramid appear. The caller's move is to fall back, not to come back.
 */
export class DziNotAvailableError extends DomainError {
  readonly httpStatus = 404;
  readonly code = 'DZI_NOT_AVAILABLE';

  constructor(assetId: string, detail?: string) {
    super(`No tile pyramid available for asset ${assetId}${detail ? `: ${detail}` : ''}`, false);
  }
}
